import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

export default function Register() {
  const [searchParams] = useSearchParams()

  // Store pending plan from URL params (for subscribe flow)
  useEffect(() => {
    const plan = searchParams.get('plan')
    if (plan) {
      sessionStorage.setItem('pending_plan', plan)
    }
  }, [searchParams])
  const [registrationType, setRegistrationType] = useState('tenant')  // business/organization only
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    company: '',
    jobTitle: '',
    companyType: '',
    companySize: '',
    companyWebsite: '',
    contactPhone: '',
    useCase: '',
    terms: false
  })
  const [errors, setErrors] = useState({})
  const [passwordStrength, setPasswordStrength] = useState(0)
  const [successMessage, setSuccessMessage] = useState('')
  const [showOtpVerification, setShowOtpVerification] = useState(false)
  const [otp, setOtp] = useState(['', '', '', '', '', ''])
  const [otpError, setOtpError] = useState('')
  const [otpLoading, setOtpLoading] = useState(false)
  const [pendingRegistrationData, setPendingRegistrationData] = useState(null)
  const [setupMode, setSetupMode] = useState('simple')  // 'simple' | 'advanced'
  const [scrapingJobId, setScrapingJobId] = useState(null)
  const [scrapingStatus, setScrapingStatus] = useState(null)
  const [showProcessingScreen, setShowProcessingScreen] = useState(false)
  const [otpVerified, setOtpVerified] = useState(false)
  const [showReferralPending, setShowReferralPending] = useState(false)

  const companyTypes = [
    { value: 'technology', label: 'Technology' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'finance', label: 'Finance' },
    { value: 'education', label: 'Education' },
    { value: 'retail', label: 'Retail' },
    { value: 'manufacturing', label: 'Manufacturing' },
    { value: 'consulting', label: 'Consulting' },
    { value: 'other', label: 'Other' }
  ]

  const companySizes = [
    { value: 'small', label: '1-10 employees' },
    { value: 'medium', label: '11-50 employees' },
    { value: 'large', label: '51-200 employees' },
    { value: 'enterprise', label: '201+ employees' }
  ]

  // Password strength checker
  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^\w\s]/.test(password)) strength++;
    return strength;
  };

  const getPasswordStrengthText = (strength) => {
    switch(strength) {
      case 0: case 1: return { text: 'Very Weak', color: 'text-red-500' };
      case 2: return { text: 'Weak', color: 'text-orange-500' };
      case 3: return { text: 'Fair', color: 'text-yellow-500' };
      case 4: return { text: 'Good', color: 'text-blue-500' };
      case 5: return { text: 'Strong', color: 'text-green-500' };
      default: return { text: '', color: '' };
    }
  };
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Poll scraping status when a job is active
  useEffect(() => {
    if (!scrapingJobId) return

    const isDev = import.meta.env.DEV || window.location.hostname === 'localhost'
    const baseUrl = isDev ? '' : (import.meta.env.VITE_API_URL?.replace(/\/api(\/v1)?$/, '') || 'https://api.cognavia.ai')

    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`${baseUrl}/api/auth/register/scraping-status/${scrapingJobId}`)
        if (!response.ok) return
        const data = await response.json()
        setScrapingStatus(data)

        // Stop polling when done
        if (data.status === 'completed' || data.status === 'failed') {
          clearInterval(pollInterval)
          // If OTP was already verified and scraping just completed, redirect
          if (otpVerified && data.status === 'completed') {
            window.location.assign('/customer-dashboard?tab=api-keys')
          } else if (otpVerified && data.status === 'failed') {
            window.location.assign('/customer-dashboard')
          }
        }
      } catch (err) {
        console.error('Scraping status poll failed:', err)
      }
    }, 3000)

    return () => clearInterval(pollInterval)
  }, [scrapingJobId, otpVerified])

  // OTP verification functions
  const handleOtpChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return; // Only allow single digits

    const newOtp = [...otp]
    newOtp[index] = value
    setOtp(newOtp)
    setOtpError('')

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.querySelector(`input[data-index="${index + 1}"]`)
      nextInput?.focus()
    }

    // Auto-submit when all digits are entered
    if (newOtp.every(digit => digit !== '') && value) {
      handleOtpVerification(newOtp.join(''))
    }
  }

  // Handle paste functionality
  const handleOtpPaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    const digits = pastedData.match(/\d/g)
    
    if (digits && digits.length >= 6) {
      const newOtp = digits.slice(0, 6)
      setOtp(newOtp)
      setOtpError('')
      
      // Focus the last input
      const lastInput = document.querySelector(`input[data-index="5"]`)
      lastInput?.focus()
      
      // Auto-submit the pasted OTP
      handleOtpVerification(newOtp.join(''))
    }
  }

  // Handle backspace navigation
  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.querySelector(`input[data-index="${index - 1}"]`)
      prevInput?.focus()
    }
  }

  const handleOtpVerification = async (code = otp.join('')) => {
    if (code.length !== 6) return

    setOtpLoading(true)
    setOtpError('')

    try {
      const endpoint = '/api/auth/verify-otp-new'
      const requestBody = { code, email: pendingRegistrationData?.email }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      })

      const data = await response.json()

      // Check for success - handle both response formats
      const isSuccess = response.ok && (data.verified || data.success)

      if (isSuccess) {
        // Registration and verification complete! Store auth data
        if (data.token) {
          localStorage.setItem('auth_token', data.token);
        }

        // Store user data
        if (data.user) {
          localStorage.setItem('user_data', JSON.stringify(data.user));
        }

        sessionStorage.removeItem('registrationData')

        // Check if simple setup scraping is still in progress
        const activeJobId = scrapingJobId || sessionStorage.getItem('scraping_job_id')
        const activeSetupMode = setupMode || sessionStorage.getItem('setup_mode')

        if (activeSetupMode === 'simple' && activeJobId) {
          setOtpVerified(true)

          if (scrapingStatus?.status === 'completed') {
            // Scraping already done - redirect to API keys
            setSuccessMessage('Email verified! Your knowledge base is ready. Redirecting...')
            setTimeout(() => {
              window.location.assign('/customer-dashboard?tab=api-keys')
            }, 1500)
          } else if (scrapingStatus?.status === 'failed') {
            // Scraping failed - go to dashboard normally
            setSuccessMessage('Email verified! Redirecting to dashboard...')
            setTimeout(() => {
              window.location.assign('/customer-dashboard')
            }, 1500)
          } else {
            // Scraping still running - show processing screen
            setShowOtpVerification(false)
            setShowProcessingScreen(true)
            setSuccessMessage('Email verified! Setting up your knowledge base...')
          }
        } else {
          // Advanced setup - check for pending plan redirect
          const pendingPlan = sessionStorage.getItem('pending_plan')
          if (pendingPlan) {
            setSuccessMessage('Email verified! Redirecting to subscribe...')
            setTimeout(() => {
              window.location.assign(`/subscribe?plan=${pendingPlan}`)
            }, 1500)
          } else {
            setSuccessMessage('Email verified successfully! Redirecting to dashboard...')
            setTimeout(() => {
              window.location.assign('/customer-dashboard')
            }, 1500)
          }
        }
      } else {
        setOtp(['', '', '', '', '', ''])
        // Handle Pydantic validation errors (array) or string errors
        let errorMsg = 'Invalid verification code'
        if (data.detail) {
          if (typeof data.detail === 'string') {
            errorMsg = data.detail
          } else if (Array.isArray(data.detail) && data.detail.length > 0) {
            errorMsg = data.detail[0].msg || 'Validation error'
          }
        } else if (data.message) {
          errorMsg = data.message
        }
        setOtpError(errorMsg)
        // Focus first input for retry
        const firstInput = document.querySelector(`input[data-index="0"]`)
        firstInput?.focus()
      }
    } catch (error) {
      setOtpError('Network error. Please try again.')
      setOtp(['', '', '', '', '', ''])
    } finally {
      setOtpLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    
    // Clear any existing registration data
    sessionStorage.removeItem('registrationData')

    try {
      // Reset errors
      setErrors({});
      
      // Validate required fields
      const newErrors = {};
      
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
      if (!formData.terms) newErrors.terms = 'You must agree to the terms and conditions';
      
      // Additional validation for tenant registration
      if (registrationType === 'tenant') {
        if (!formData.company.trim()) newErrors.company = 'Company name is required';
        if (!formData.companyWebsite.trim()) newErrors.companyWebsite = 'Company website is required';
      }
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (formData.email && !emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
      }

      // Password strength validation
      if (formData.password && passwordStrength < 3) {
        newErrors.password = 'Password is too weak. Please choose a stronger password.';
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Normalize URL: add https:// if user typed www.example.com or example.com
      let normalizedWebsite = formData.companyWebsite.trim();
      if (normalizedWebsite && !/^https?:\/\//i.test(normalizedWebsite)) {
        normalizedWebsite = 'https://' + normalizedWebsite;
      }

      const registrationData = {
        admin_name: `${formData.firstName} ${formData.lastName}`.trim(),
        admin_email: formData.email,
        admin_password: formData.password,
        company_name: formData.company,
        company_website: normalizedWebsite,
        company_size: formData.companySize || null,
        use_case: formData.useCase || null,
        contact_phone: formData.contactPhone || null,
        setup_mode: setupMode
      }
      const endpoint = '/api/auth/register/tenant'

      // Store registration data for OTP verification
      sessionStorage.setItem('registrationData', JSON.stringify({...registrationData, type: registrationType}));

      // Use proxy route for development, production API for production
      const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development' || window.location.hostname === 'localhost'
      // Strip /api or /api/v1 suffix from VITE_API_URL since endpoint already includes /api
      const baseApiUrl = isDevelopment ? '' : (import.meta.env.VITE_API_URL?.replace(/\/api(\/v1)?$/, '') || 'https://api.cognavia.ai')
      const fullEndpoint = `${baseApiUrl}${endpoint}`

      const response = await fetch(fullEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      })

      let result;
      const responseText = await response.text();

      // Try to parse JSON, but handle non-JSON responses gracefully
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        console.error('Failed to parse response as JSON:', parseError);
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        throw new Error(result.detail || result.message || `Registration failed with status ${response.status}`)
      }
      
      if (result.success) {
        // Check if this is a referral flow (personal email)
        if (result.requires_referral) {
          setShowReferralPending(true);
          setError('');
          return;
        }

        // Store registration data for OTP verification
        const updatedData = {
          ...registrationData,
          type: registrationType,
          user_id: result.user_id,
          tenant_id: result.tenant_id
        };
        sessionStorage.setItem('registrationData', JSON.stringify(updatedData));

        // Show success message and inline OTP verification
        setError('');
        let message;

        if (result.message && result.message.includes("account exists but wasn't verified")) {
          message = 'Account found! A new verification code has been sent to your email.';
        } else {
          message = registrationType === 'tenant'
            ? 'Account created successfully! Please verify your email to complete setup.'
            : 'Account created successfully! Please verify your email.';
        }

        setSuccessMessage(message);

        // Store registration data for OTP verification
        const userEmail = registrationData.admin_email;
        setPendingRegistrationData({
          email: userEmail,
          type: registrationType,
          sessionToken: result.sessionToken,
          result: result
        });

        // Simple Setup: start background scraping immediately (only for business email/OTP flow)
        if (setupMode === 'simple' && formData.companyWebsite && result.user_id) {
          try {
            const scrapeResponse = await fetch(`${baseApiUrl}/api/auth/register/start-scraping`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: result.user_id,
                url: formData.companyWebsite
              })
            })
            const scrapeResult = await scrapeResponse.json()
            if (scrapeResult.success) {
              setScrapingJobId(scrapeResult.job_id)
              sessionStorage.setItem('scraping_job_id', scrapeResult.job_id)
              sessionStorage.setItem('setup_mode', 'simple')
            }
          } catch (scrapeError) {
            console.error('Failed to start background scraping:', scrapeError)
            // Non-fatal: user can still complete registration normally
          }
        }

        // Show inline OTP verification instead of redirecting
        setShowOtpVerification(true);

        return; // Exit early
      } else {
        throw new Error(result.message || result.error || 'Registration failed. Please try again.');
      }

    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData({
      ...formData,
      [name]: newValue
    });

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }

    // Update password strength in real-time
    if (name === 'password') {
      setPasswordStrength(checkPasswordStrength(value));

      // Also validate confirmPassword if it has a value
      if (formData.confirmPassword && value !== formData.confirmPassword) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: 'Passwords do not match'
        }));
      } else if (formData.confirmPassword && value === formData.confirmPassword) {
        // Clear confirmPassword error when passwords match
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          return newErrors;
        });
      }
    }

    // Validate confirmPassword when it changes
    if (name === 'confirmPassword' && formData.password) {
      if (value !== formData.password) {
        setErrors(prev => ({
          ...prev,
          confirmPassword: 'Passwords do not match'
        }));
      } else {
        // Clear error when passwords match
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.confirmPassword;
          return newErrors;
        });
      }
    }
  };
  
  const handleBlur = (e) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };
    
    // Validate on blur
    switch(name) {
      case 'firstName':
        if (!value.trim()) newErrors.firstName = 'First name is required';
        break;
      case 'lastName':
        if (!value.trim()) newErrors.lastName = 'Last name is required';
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) {
          newErrors.email = 'Email is required';
        } else if (!emailRegex.test(value)) {
          newErrors.email = 'Please enter a valid email address';
        } else {
          delete newErrors.email;
        }
        break;
      case 'confirmPassword':
        if (value !== formData.password) {
          newErrors.confirmPassword = 'Passwords do not match';
        } else {
          // Clear the error when passwords match
          delete newErrors.confirmPassword;
        }
        break;
    }
    
    setErrors(newErrors);
  }


  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-red-600">CognaAssist</h1>
            </div>
            <Link 
              to="/login" 
              className="text-sm text-gray-600 hover:text-gray-900 font-medium"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>

      <div className="flex">
        {/* Left side - Hero content */}
        <div className="hidden lg:block lg:w-1/2 bg-gray-50 px-12 py-16">
          <div className="max-w-lg mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Transform your customer support with AI
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              CognaAssist provides intelligent, instant customer support that learns from your website content and delivers personalized responses 24/7.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">5-minute setup with simple widget integration</span>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">Automatically trained on your website content</span>
              </div>
              <div className="flex items-center">
                <div className="flex-shrink-0 w-5 h-5 bg-green-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <span className="text-gray-700">Real-time analytics and performance insights</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Registration form */}
        <div className="w-full lg:w-1/2 px-6 py-16">
          <div className="max-w-md mx-auto">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900">Create your account</h2>
              <p className="mt-2 text-gray-600">Get started with CognaAssist in just a few minutes</p>
            </div>

            {/* Setup Mode Toggle */}
            {registrationType && !showOtpVerification && !showProcessingScreen && !showReferralPending && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Setup Mode</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setSetupMode('simple')}
                    className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                      setupMode === 'simple'
                        ? 'bg-red-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Simple Setup
                    <span className={`block text-xs font-normal mt-0.5 ${setupMode === 'simple' ? 'text-red-100' : 'text-gray-500'}`}>
                      Auto-create knowledge base
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSetupMode('advanced')}
                    className={`flex-1 py-2.5 px-4 text-sm font-medium transition-colors ${
                      setupMode === 'advanced'
                        ? 'bg-red-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Advanced Setup
                    <span className={`block text-xs font-normal mt-0.5 ${setupMode === 'advanced' ? 'text-red-100' : 'text-gray-500'}`}>
                      Configure manually later
                    </span>
                  </button>
                </div>
                {setupMode === 'simple' && (
                  <p className="mt-2 text-xs text-gray-500">
                    We'll automatically scrape your company website and create a knowledge base while you verify your email. You can start using CognaAssist right away.
                  </p>
                )}
              </div>
            )}

            {/* Business/Organization registration */}

            {registrationType && !showOtpVerification && !showProcessingScreen && !showReferralPending && (
            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-800 text-sm font-medium">{error}</p>
                  </div>
                </div>
              )}
              
              {successMessage && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex">
                    <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <p className="text-green-800 text-sm font-medium">{successMessage}</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.firstName 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                    }`}
                    placeholder="John"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.lastName 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                    }`}
                    placeholder="Doe"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                  )}
                </div>
              </div>
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.email
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                  }`}
                  placeholder="john@yourcompany.com"
                />
                <p className="mt-1 text-xs text-gray-500">Business email for instant access. Personal emails require admin approval.</p>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    id="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.password 
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                        : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                    }`}
                    placeholder="••••••••"
                  />
                  {formData.password && (
                    <div className="mt-1">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Strength:</span>
                        <span className={`text-xs font-medium ${getPasswordStrengthText(passwordStrength).color}`}>
                          {getPasswordStrengthText(passwordStrength).text}
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
                        <div 
                          className={`h-1 rounded-full transition-all duration-300 ${
                            passwordStrength <= 1 ? 'bg-red-500' :
                            passwordStrength <= 2 ? 'bg-orange-500' :
                            passwordStrength <= 3 ? 'bg-yellow-500' :
                            passwordStrength <= 4 ? 'bg-blue-500' : 'bg-green-500'
                          }`}
                          style={{ width: `${(passwordStrength / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  )}
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password *
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    id="confirmPassword"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                      errors.confirmPassword
                        ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                        : formData.confirmPassword && formData.password && formData.confirmPassword === formData.password
                          ? 'border-green-400 focus:ring-green-500 focus:border-green-500'
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                    }`}
                    placeholder="••••••••"
                  />
                  {errors.confirmPassword && (
                    <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                  )}
                  {!errors.confirmPassword && formData.confirmPassword && formData.password && formData.confirmPassword === formData.password && (
                    <p className="mt-1 text-sm text-green-600">Passwords match</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700 mb-2">
                  {registrationType === 'tenant' ? 'Company Name *' : 'Company Name (Optional)'}
                </label>
                <input
                  type="text"
                  name="company"
                  id="company"
                  required={registrationType === 'tenant'}
                  value={formData.company}
                  onChange={handleChange}
                  className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                    registrationType === 'tenant' && errors.company
                      ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                      : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                  }`}
                  placeholder={registrationType === 'tenant' ? 'Acme Corporation' : 'Your Company Inc.'}
                />
                {registrationType === 'tenant' && errors.company && (
                  <p className="mt-1 text-sm text-red-600">{errors.company}</p>
                )}
              </div>
              
              {registrationType === 'tenant' && (
                <>
                  <div>
                    <label htmlFor="companyWebsite" className="block text-sm font-medium text-gray-700 mb-2">
                      Company Website *
                    </label>
                    <input
                      type="text"
                      name="companyWebsite"
                      id="companyWebsite"
                      required
                      value={formData.companyWebsite}
                      onChange={handleChange}
                      className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
                        errors.companyWebsite
                          ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                          : 'border-gray-300 focus:ring-red-500 focus:border-red-500'
                      }`}
                      placeholder="www.your-company.com"
                    />
                    {errors.companyWebsite && (
                      <p className="mt-1 text-sm text-red-600">{errors.companyWebsite}</p>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                        Contact Phone
                      </label>
                      <input
                        type="tel"
                        name="contactPhone"
                        id="contactPhone"
                        value={formData.contactPhone}
                        onChange={handleChange}
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        placeholder="+1-555-0123"
                      />
                    </div>
                    <div>
                      <label htmlFor="companySize" className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size
                      </label>
                      <select
                        name="companySize"
                        id="companySize"
                        value={formData.companySize}
                        onChange={handleChange}
                        className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      >
                        <option value="">Select size...</option>
                        {companySizes.map(size => (
                          <option key={size.value} value={size.value}>{size.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="useCase" className="block text-sm font-medium text-gray-700 mb-2">
                      Primary Use Case
                    </label>
                    <textarea
                      name="useCase"
                      id="useCase"
                      rows={3}
                      value={formData.useCase}
                      onChange={handleChange}
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                      placeholder="Describe how you plan to use CognaAssist (e.g., customer support, product information, etc.)"
                    />
                  </div>
                </>
              )}
              

              <div>
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    name="terms"
                    id="terms"
                    checked={formData.terms}
                    onChange={handleChange}
                    className={`h-4 w-4 text-red-600 focus:ring-red-500 rounded mt-1 ${
                      errors.terms ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  <label htmlFor="terms" className="ml-2 block text-sm text-gray-900">
                    I agree to the{' '}
                    <a href="/terms" className="text-red-600 hover:text-red-500 underline">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" className="text-red-600 hover:text-red-500 underline">
                      Privacy Policy
                    </a>
                  </label>
                </div>
                {errors.terms && (
                  <p className="mt-1 text-sm text-red-600">{errors.terms}</p>
                )}
              </div>

              <div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-red-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      {successMessage ? 'Redirecting...' : `Creating ${registrationType} account...`}
                    </div>
                  ) : (
                    `Create ${registrationType === 'tenant' ? 'Business' : 'Individual'} Account`
                  )}
                </button>
              </div>
            </form>
            )}

            {/* Inline OTP Verification */}
            {showOtpVerification && (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Verify Your Email
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    We sent a 6-digit code to <strong>{pendingRegistrationData?.email}</strong>
                  </p>
                </div>

                {/* Success Message */}
                {successMessage && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex">
                      <svg className="w-5 h-5 text-green-400 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="text-green-800 text-sm font-medium">{successMessage}</p>
                    </div>
                  </div>
                )}

                {/* OTP Input Fields */}
                <div className="flex justify-center space-x-3">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength="1"
                      value={digit}
                      data-index={index}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      className="w-12 h-12 text-center text-xl font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 border-gray-300"
                      autoFocus={index === 0}
                      placeholder="0"
                    />
                  ))}
                </div>

                {/* OTP Error Message */}
                {otpError && (
                  <div className="text-sm text-red-600 text-center">
                    {otpError}
                  </div>
                )}

                {/* Manual Verify Button */}
                <button
                  onClick={() => handleOtpVerification()}
                  disabled={otp.some(digit => !digit) || otpLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {otpLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Verifying...
                    </div>
                  ) : (
                    'Verify Email'
                  )}
                </button>

                {/* Scraping Progress During OTP (Simple Setup) */}
                {scrapingJobId && scrapingStatus && scrapingStatus.status !== 'completed' && scrapingStatus.status !== 'failed' && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center mb-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                      <span className="text-sm font-medium text-blue-800">{scrapingStatus.stage || 'Setting up knowledge base...'}</span>
                    </div>
                    <div className="w-full bg-blue-200 rounded-full h-1.5">
                      <div
                        className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                        style={{ width: `${scrapingStatus.progress || 0}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {/* Back to Registration */}
                <div className="text-center">
                  <button
                    onClick={() => {
                      setShowOtpVerification(false)
                      setOtp(['', '', '', '', '', ''])
                      setOtpError('')
                      setSuccessMessage('')
                      setPendingRegistrationData(null)
                    }}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    ← Back to Registration
                  </button>
                </div>
              </div>
            )}

            {/* Referral Pending Screen - shown for personal email registrations */}
            {showReferralPending && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Registration Pending Approval
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Since you registered with a personal email, our admin team will review your registration. You'll receive an email with a referral code once approved.
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-amber-800 mb-2">What happens next?</h4>
                  <ol className="text-sm text-amber-700 space-y-2 list-decimal list-inside">
                    <li>Our admin team reviews your registration</li>
                    <li>Once approved, you'll receive a referral code via email</li>
                    <li>Go to <strong>chat.cognavia.ai</strong> and enter your referral code</li>
                    <li>You'll be redirected to your dashboard</li>
                  </ol>
                </div>

                <p className="text-xs text-gray-500 text-center">
                  This typically takes less than 24 hours. Check your email for updates.
                </p>

                <div className="text-center">
                  <Link
                    to="/"
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    Back to Home
                  </Link>
                </div>
              </div>
            )}

            {/* Processing Screen - shown after OTP verified while scraping still in progress */}
            {showProcessingScreen && (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    Email Verified Successfully!
                  </h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Setting up your AI knowledge base from <strong>{formData.companyWebsite}</strong>
                  </p>
                </div>

                {/* Progress indicator */}
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {scrapingStatus?.stage || 'Starting...'}
                    </span>
                    <span className="text-sm text-gray-500">{scrapingStatus?.progress || 0}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div
                      className="bg-red-600 h-2.5 rounded-full transition-all duration-500"
                      style={{ width: `${scrapingStatus?.progress || 0}%` }}
                    ></div>
                  </div>
                  {scrapingStatus?.chunks_count && (
                    <p className="text-xs text-gray-500 mt-2">
                      Processing {scrapingStatus.chunks_count} content chunks
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600 mr-3"></div>
                  <span className="text-sm text-gray-600">This usually takes 1-3 minutes</span>
                </div>

                {/* Skip button */}
                <div className="text-center">
                  <button
                    onClick={() => window.location.assign('/customer-dashboard')}
                    className="text-sm text-gray-500 hover:text-gray-700 underline"
                  >
                    Skip and go to dashboard
                  </button>
                </div>

                {/* Error handling */}
                {scrapingStatus?.status === 'failed' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                    <p className="text-sm text-red-800 mb-2">
                      Knowledge base creation encountered an issue. You can set it up manually from the dashboard.
                    </p>
                    <button
                      onClick={() => window.location.assign('/customer-dashboard?tab=knowledge-base')}
                      className="text-sm text-red-600 hover:text-red-700 font-medium"
                    >
                      Go to Dashboard
                    </button>
                  </div>
                )}
              </div>
            )}

            <p className="mt-6 text-center text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-red-600 hover:text-red-700 font-medium">
                Sign in
              </Link>
            </p>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                By signing up, you agree to our Terms of Service and Privacy Policy. 
                No credit card required for trial.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}